import React from 'react';
import PropTypes from 'prop-types';
import styles from '../styles/PickItem.module.css';
import { formatGameDateTime } from '../utils/timeUtils';
import { getLineMovementSentiment } from '../utils/lineMovementUtils';
import { getWatchListDisplayLines } from '../utils/lineDataUtils';

const LockOpenIcon = () => (
  <svg className={styles.lockIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
    <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/>
  </svg>
);

const LockClosedIcon = () => (
  <svg className={styles.lockIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
  </svg>
);

const BackspaceIcon = () => (
  <svg className={styles.backspaceIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
    <path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H7.07L2.4 12l4.66-7H22v14zm-11.59-2L14 13.41 17.59 17 19 15.59 15.41 12 19 8.41 17.59 7 14 10.59 10.41 7 9 8.41 12.59 12 9 15.59z"/>
  </svg>
);

const PickItem = ({
  pick,
  onRemove,
  onLock,
  onTogglePending,
  isPending,
  isDisabled,
  isFutureWeek,
  futureWeekLockAllowed,
  showPadlockLock,
}) => {
  if (pick.pickType === 'bye') {
    return (
      <div className={`${styles.pickItem} ${styles.byePickItem}`}>
        <div className={styles.pickContent}>
          <div className={styles.watchedLabel}>Bye Week</div>
          <div className={styles.primaryInfo}>
            <button
              className={`${styles.lockIconContainer}
                        ${isPending ? styles.pending : ''}
                        ${isDisabled ? styles.disabled : ''}`}
              onClick={() => !isDisabled && onTogglePending({
                ...pick,
                status: isPending ? 'watched' : 'pending'
              })}
              disabled={isDisabled}
              aria-label="Toggle bye week pending status"
              type="button"
            >
              {isPending ? <LockClosedIcon /> : <LockOpenIcon />}
            </button>
            <div className={styles.byeDetails}>
              <span className={styles.byeTitle}>Use Bye Week</span>
              <span className={styles.byeDescription}>
                This will count as your one bye week for the season
              </span>
            </div>
          </div>
        </div>
        {isPending && onLock && (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.lockActionButton}
              onClick={() => onLock(pick)}
              aria-label="Lock bye week"
            >
              Lock
            </button>
          </div>
        )}
      </div>
    );
  }

  const formatLine = (line) => {
    if (line === undefined || line === null) return 'N/A';
    return line > 0 ? `+${line}` : line.toString();
  };

  const { watchLine: displayWatchLine, liveLine: displayLiveLine } = getWatchListDisplayLines(pick);

  const renderLineMovement = () => {
    const sentiment = getLineMovementSentiment(pick.pickType, displayWatchLine, displayLiveLine);
    if (sentiment === 'neutral') return null;

    const lineMovement = (displayLiveLine || 0) - (displayWatchLine || 0);
    const arrowDirection = lineMovement < 0 ? '↘' : '↗';
    const colorClass = sentiment === 'good' ? styles.lineGood : styles.lineBad;

    return (
      <span className={`${styles.lineMovement} ${colorClass}`}>
        {arrowDirection}
      </span>
    );
  };

  const getPickName = () => {
    if (pick.pickType === 'over' || pick.pickType === 'under') {
      return pick.pickType.charAt(0).toUpperCase() + pick.pickType.slice(1);
    }
    return pick.team || 'Unknown Team';
  };

  const { displayDate } = formatGameDateTime(pick.gameDate);

  const isLockDisabled = isDisabled || (isFutureWeek && !futureWeekLockAllowed);

  const getLockTooltip = () => {
    if (isFutureWeek && !futureWeekLockAllowed) {
      return 'Future week locks disabled';
    }
    if (isDisabled) {
      return 'Cannot lock this pick';
    }
    return 'Lock this pick';
  };

  return (
    <div className={`${styles.pickItem} ${pick.status === 'locking' ? styles.locking : ''}`}>
      {pick.status === 'locking' && (
        <div className={styles.overlay}>
          <span>Locking...</span>
        </div>
      )}
      <div className={styles.pickContent}>
        <div className={styles.watchedLabel}>Watched Pick</div>
        <div className={styles.primaryInfo}>
          {showPadlockLock && onLock && (
            <button
              type="button"
              className={`${styles.lockIconContainer} ${isLockDisabled ? styles.disabled : ''}`}
              onClick={() => !isLockDisabled && onLock(pick)}
              disabled={isLockDisabled}
              aria-label={getLockTooltip()}
              title={getLockTooltip()}
            >
              <LockOpenIcon />
            </button>
          )}
          <span className={styles.pickDetails}>
            <span className={styles.team}>{getPickName()}</span>
            <span className={styles.watchLine}>{formatLine(displayWatchLine)},</span>
            <span className={styles.currentLine}>
              Current: {formatLine(displayLiveLine)} {renderLineMovement()}
            </span>
          </span>
        </div>
        <div className={styles.gameInfo}>
          {`${pick.pickType === 'away' ? pick.team : pick.opponent} @
          ${pick.pickType === 'away' ? pick.opponent : pick.team}, ${displayDate}`}
        </div>
      </div>
      <div className={styles.actions}>
        {onLock && !showPadlockLock && (
          <button
            type="button"
            className={`${styles.lockActionButton} ${isLockDisabled ? styles.disabled : ''}`}
            onClick={() => !isLockDisabled && onLock(pick)}
            disabled={isLockDisabled}
            aria-label={getLockTooltip()}
            title={getLockTooltip()}
          >
            Lock
          </button>
        )}
        <button
          type="button"
          className={`${styles.actionButton} ${styles.removeButton}`}
          onClick={() => onRemove(pick.id)}
          title="Unwatch this Pick"
          aria-label="Unwatch this Pick"
        >
          <BackspaceIcon />
        </button>
      </div>
    </div>
  );
};

PickItem.propTypes = {
  pick: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    team: PropTypes.string,
    opponent: PropTypes.string,
    gameDate: PropTypes.string,
    watchLine: PropTypes.number,
    liveLine: PropTypes.number,
    status: PropTypes.string,
    pickType: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onLock: PropTypes.func,
  onTogglePending: PropTypes.func,
  isDisabled: PropTypes.bool.isRequired,
  isPending: PropTypes.bool,
  isFutureWeek: PropTypes.bool,
  futureWeekLockAllowed: PropTypes.bool,
  showPadlockLock: PropTypes.bool,
};

PickItem.defaultProps = {
  onLock: null,
  onTogglePending: null,
  isPending: false,
  isFutureWeek: false,
  futureWeekLockAllowed: true,
  showPadlockLock: false,
};

export default PickItem;
